import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { 
  ArrowLeft, 
  BookOpen,
  Coins,
  ChevronLeft,
  ChevronRight,
  Target,
  Trophy,
  Star,
  Lightbulb,
  XCircle,
  Lock,
  Search,
  Play,
  Clock,
  CheckCircle,
  AlertCircle,
  Settings,
  RefreshCw,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IfaSignsGame = () => {
  const { token, user } = useContext(AuthContext);
  
  // Game state management
  const [gameState, setGameState] = useState('menu'); // menu, difficulty, topics, odduns, custom_odduns, question_config, playing, completed, study, encyclopedia
  const [difficulty, setDifficulty] = useState('beginner');
  const [loading, setLoading] = useState(false);
  const [topicSelectionMode, setTopicSelectionMode] = useState('single'); // 'single', 'multiple', 'random'
  
  // Game configuration
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedOddunSet, setSelectedOddunSet] = useState('meyi'); // 'meyi', 'random', 'custom'
  const [selectedOdduns, setSelectedOdduns] = useState([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [gameMode, setGameMode] = useState('multiple_choice'); // 'multiple_choice', 'true_false', 'matching'
  
  // Game session state
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [timeLeft, setTimeLeft] = useState(60);
  const [wrongAnswers, setWrongAnswers] = useState(0);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [gameResults, setGameResults] = useState(null);
  
  // UI state
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const [currentQuestionData, setCurrentQuestionData] = useState(null);
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState(null);
  
  // Study/Encyclopedia mode state (keeping backward compatibility)
  const [odduns, setOdduns] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cowrieBalance, setCowrieBalance] = useState(user?.cowrie_shells || 0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOdduns, setFilteredOdduns] = useState([]);
  const [currentOddunDetails, setCurrentOddunDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Modal and contribution state
  const [showContributionModal, setShowContributionModal] = useState(false);
  const [contributionSection, setContributionSection] = useState('');
  const [contributionType, setContributionType] = useState('improvement');
  const [contributionText, setContributionText] = useState('');
  const [showSecretRitual, setShowSecretRitual] = useState(false);
  const [secretRitualData, setSecretRitualData] = useState(null);

  // Game difficulty levels configuration
  const difficulties = [
    {
      value: 'beginner',
      label: 'Principiante',
      description: 'Aprende 16 odduns de Ifa',
      oddunCount: 16,
      timeLimit: 60,
      maxErrors: 10,
      icon: '📚',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      value: 'intermediate',
      label: 'Intermedio', 
      description: 'Domina 64 diferentes Odduns del sistema Ifá',
      oddunCount: 64,
      timeLimit: 45,
      maxErrors: 8,
      icon: '🎓',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      value: 'advanced',
      label: 'Avanzado',
      description: 'Explora 128 Odduns y sus combinaciones',
      oddunCount: 128,
      timeLimit: 30,
      maxErrors: 6,
      icon: '🔬',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      value: 'expert',
      label: 'Experto',
      description: 'Domina los 256 Odduns completos del sistema',
      oddunCount: 256,
      timeLimit: 15,
      maxErrors: 3,
      icon: '🏆',
      color: 'from-violet-500 to-purple-700'
    }
  ];

  // Game topics configuration
  const gameTopics = [
    {
      id: 'recognition',
      name: 'Reconocimiento de Odduns',
      description: 'Reconoce Odduns por su nombre y características',
      icon: '🎯',
      difficulty: 'easy'
    },
    {
      id: 'sureye',
      name: 'Sureyes de los Odduns',
      description: 'Asocia Odduns con sus historias sagradas',
      icon: '📜',
      difficulty: 'medium'
    },
    {
      id: 'born_in_sign',
      name: 'Lo que Nace en el Signo',
      description: 'Identifica qué nace en cada Oddun',
      icon: '🌱',
      difficulty: 'medium'
    },
    {
      id: 'prohibitions',
      name: 'Prohibiciones',
      description: 'Asocia Odduns con sus tabúes',
      icon: '🚫',
      difficulty: 'medium'
    },
    {
      id: 'elements',
      name: 'Elementos Asociados',
      description: 'Relaciona Odduns con elementos, colores, etc.',
      icon: '🌟',
      difficulty: 'medium'
    },
    {
      id: 'spiritual',
      name: 'Significado Espiritual',
      description: 'Comprende el significado espiritual profundo',
      icon: '✨',
      difficulty: 'hard'
    },
    {
      id: 'binary_patterns',
      name: 'Patrones Binarios',
      description: 'Reconoce patrones binarios de Odduns',
      icon: '⚫',
      difficulty: 'easy'
    },
    {
      id: 'refranes',
      name: 'Refranes',
      description: 'Asocia Odduns con sus refranes tradicionales',
      icon: '💭',
      difficulty: 'hard'
    },
    {
      id: 'patakines',
      name: 'Patakines',
      description: 'Relaciona Odduns con sus leyendas sagradas',
      icon: '📚',
      difficulty: 'hard'
    }
  ];

  // Oddun selection options
  const oddunSelectionOptions = [
    {
      id: 'meyi',
      name: 'Odduns Meyi',
      description: 'Los primeros 16 Odduns principales'
    },
    {
      id: 'sequential',
      name: 'Secuencial',
      description: 'Primeros X Odduns en orden'
    },
    {
      id: 'random',
      name: 'Aleatorio',
      description: 'Selección aleatoria de Odduns'
    },
    {
      id: 'custom',
      name: 'Selección Personalizada',
      description: 'Elige tus propios Odduns'
    }
  ];

  // Question count options
  const questionCounts = [10, 20, 35, 50];

  // Game modes
  const gameModes = [
    {
      id: 'multiple_choice',
      name: 'Opción Múltiple',
      description: 'Elige la respuesta correcta entre 4 opciones',
      icon: '🔤'
    },
    {
      id: 'true_false',
      name: 'Verdadero/Falso',
      description: 'Determina si la afirmación es correcta',
      icon: '✅'
    },
    {
      id: 'matching',
      name: 'Emparejar',
      description: 'Conecta elementos relacionados',
      icon: '🔗'
    }
  ];

  // Handle difficulty selection
  const handleDifficultySelect = (selectedDifficulty) => {
    setDifficulty(selectedDifficulty);
    
    if (selectedDifficulty === 'encyclopedia') {
      // Go directly to encyclopedia study mode
      loadEncyclopediaData();
    } else {
      // Go to topics selection for game mode
      setGameState('topics');
    }
  };

  // Load encyclopedia data for study mode
  const loadEncyclopediaData = async () => {
    setLoading(true);
    try {
      // Use the original public API which now includes rezo and rezo_personalizado fields
      const response = await axios.get(`${API}/ifa-signs/odduns?level=expert`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOdduns(response.data.odduns || []);
      setCurrentIndex(0);
      setGameState('study');
    } catch (error) {
      console.error('Error loading encyclopedia data:', error);
      toast.error('Error al cargar los datos de la enciclopedia');
    } finally {
      setLoading(false);
    }
  };

  // Handle topic selection
  const handleTopicSelection = (topicId) => {
    if (selectedTopics.includes(topicId)) {
      setSelectedTopics(prev => prev.filter(id => id !== topicId));
    } else {
      setSelectedTopics(prev => [...prev, topicId]);
    }
  };

  // Start the game
  const startGame = async () => {
    if (selectedTopics.length === 0) {
      toast.error('Por favor selecciona al menos un tema');
      return;
    }

    setLoading(true);
    try {
      const currentDifficulty = difficulties.find(d => d.value === difficulty);
      
      const gameConfig = {
        difficulty: difficulty,
        topics: selectedTopics,
        oddun_set: selectedOddunSet,
        selected_odduns: selectedOddunSet === 'custom' ? selectedOdduns : [],
        question_count: questionCount,
        game_mode: gameMode,
        time_limit: currentDifficulty.timeLimit,
        max_errors: currentDifficulty.maxErrors
      };

      const response = await axios.post(`${API}/games/ifa-signs/start`, gameConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSessionId(response.data.session_id);
      setQuestions(response.data.questions);
      setCurrentQuestionIndex(0);
      setScore({ correct: 0, total: 0 });
      setTimeLeft(currentDifficulty.timeLimit);
      setWrongAnswers(0);
      setIsDisqualified(false);
      setGameState('playing');
      
      // Start timer if there's a time limit
      if (currentDifficulty.timeLimit) {
        startTimer();
      }

    } catch (error) {
      console.error('Error starting game:', error);
      toast.error('Error al iniciar el juego');
    } finally {
      setLoading(false);
    }
  };

  // Timer function
  const startTimer = () => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handle time up
  const handleTimeUp = () => {
    toast.error('¡Tiempo agotado!');
    handleAnswerSubmit('', true); // Submit empty answer for timeout
  };

  // Handle answer submission
  const handleAnswerSubmit = async (answer, isTimeout = false) => {
    if (!sessionId || !questions[currentQuestionIndex]) return;

    try {
      const response = await axios.post(`${API}/games/ifa-signs/answer`, {
        session_id: sessionId,
        question_id: questions[currentQuestionIndex].id,
        answer: answer,
        is_timeout: isTimeout
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const isCorrect = response.data.correct;
      setLastAnswerCorrect(isCorrect);
      setShowResult(true);

      // Update score
      setScore(prev => ({
        correct: prev.correct + (isCorrect ? 1 : 0),
        total: prev.total + 1
      }));

      // Update wrong answers count
      if (!isCorrect) {
        setWrongAnswers(prev => prev + 1);
        const currentDifficulty = difficulties.find(d => d.value === difficulty);
        if (currentDifficulty.maxErrors && wrongAnswers + 1 >= currentDifficulty.maxErrors) {
          setIsDisqualified(true);
          toast.error('¡Has sido descalificado por demasiados errores!');
          setGameState('completed');
          return;
        }
      }

      // Auto advance to next question after showing result
      const timer = setTimeout(() => {
        setShowResult(false);
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
          setSelectedAnswer('');
          setSelectedAnswers([]);
        } else {
          // Game completed
          setGameState('completed');
          setGameResults(response.data.game_results);
        }
      }, 2000);

      setAutoAdvanceTimer(timer);

    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Error al enviar la respuesta');
    }
  };

  // Fetch comprehensive data for current Oddun
  const fetchOddunDetails = async (oddunId) => {
    if (!oddunId) return;
    
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/ifa-signs/oddun/${oddunId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentOddunDetails(response.data);
    } catch (error) {
      console.error('Error loading Oddun details:', error);
      // Fallback to list data if individual fetch fails
      setCurrentOddunDetails(odduns[currentIndex]);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handle Oddun navigation
  const handleOddunChange = (newIndex) => {
    setCurrentIndex(newIndex);
    if (odduns[newIndex]) {
      fetchOddunDetails(odduns[newIndex].id);
    }
  };

  // Effect to fetch details when current Oddun changes
  useEffect(() => {
    if (odduns.length > 0 && odduns[currentIndex]) {
      fetchOddunDetails(odduns[currentIndex].id);
    }
  }, [currentIndex, odduns]);

  // Purchase secret content
  const handlePurchaseSecret = async (oddunId) => {
    if (cowrieBalance < 100) {
      toast.error('No tienes suficientes conchas de cowrie para desbloquear este secreto.');
      return;
    }

    try {
      const response = await axios.post(`${API}/ifa-signs/purchase-secret`, {
        oddun_id: oddunId,
        cost: 100
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Update user's cowrie balance and purchased secrets
        setCowrieBalance(response.data.new_balance);
        // Update user object with new purchased secrets
        const updatedUser = { 
          ...user, 
          cowrie_shells: response.data.new_balance,
          purchased_secrets: [...(user?.purchased_secrets || []), oddunId]
        };
        // Update user context if available
        toast.success('¡Secreto desbloqueado exitosamente! 🔮');
        
        // Force re-render to show the secret content
        window.location.reload();
      }
    } catch (error) {
      console.error('Error purchasing secret:', error);
      toast.error('Error al desbloquear el secreto. Inténtalo de nuevo.');
    }
  };

  // Helper function to get data source information
  const getDataSourceInfo = (oddun, fieldName = '') => {
    const source = oddun?.data_source || 'unknown';
    
    const sourceMap = {
      'comprehensive_traditional': {
        label: 'Base Tradicional',
        icon: '📚',
        color: 'bg-green-100 text-green-700',
        description: 'Datos tradicionales completos'
      },
      'ODDUNS1_traditional_authentic': {
        label: 'ODDUNS1.txt',
        icon: '📜',
        color: 'bg-purple-100 text-purple-700',
        description: 'Tratado Enciclopédico de Ifá - Vol. 1'
      },
      'ODDUNS2_traditional_authentic': {
        label: 'ODDUNS2.txt',
        icon: '📜',
        color: 'bg-purple-100 text-purple-700',
        description: 'Tratado Enciclopédico de Ifá - Vol. 2'
      },
      'excel_corrected': {
        label: 'Excel Oficial',
        icon: '📊',
        color: 'bg-blue-100 text-blue-700',
        description: '256 Odduns de IFA - Archivo maestro'
      },
      'generated': {
        label: 'Generado',
        icon: '⚙️',
        color: 'bg-gray-100 text-gray-700',
        description: 'Estructura base generada'
      },
      'manual': {
        label: 'Manual',
        icon: '✏️',
        color: 'bg-yellow-100 text-yellow-700',
        description: 'Ingreso manual de administrador'
      }
    };

    return sourceMap[source] || {
      label: source || 'Desconocido',
      icon: '❓',
      color: 'bg-gray-100 text-gray-600',
      description: 'Fuente desconocida'
    };
  };

  // Helper function to render data source badge
  const renderDataSourceBadge = (oddun, fieldName = '', position = 'top-right') => {
    const sourceInfo = getDataSourceInfo(oddun, fieldName);
    
    const positionClasses = {
      'top-right': 'absolute top-2 right-2',
      'bottom-left': 'mt-2',
      'inline': 'inline-flex ml-2'
    };

    return (
      <div 
        className={`${positionClasses[position]} ${sourceInfo.color} px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 shadow-sm z-10`}
        title={`${sourceInfo.description} - Campo: ${fieldName || 'general'}`}
      >
        <span>{sourceInfo.icon}</span>
        <span>{sourceInfo.label}</span>
      </div>
    );
  };
  // Handle search filtering for Encyclopedia mode
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredOdduns(odduns);
      return;
    }
    
    const filtered = odduns.filter((oddun) => {
      const searchTerm = query.toLowerCase().trim();
      const oddunName = oddun.name?.toLowerCase() || '';
      const oddunPosition = oddun.position?.toString() || '';
      const oddunId = oddun.id?.toString().toLowerCase() || '';
      const traditionalSubtitle = oddun.traditional_subtitle?.toLowerCase() || '';
      const traditionalName = oddun.traditional_name?.toLowerCase() || '';
      
      return (
        oddunName.includes(searchTerm) ||
        oddunPosition.includes(searchTerm) ||
        oddunId.includes(searchTerm) ||
        traditionalSubtitle.includes(searchTerm) ||
        traditionalName.includes(searchTerm) ||
        // Also search for individual words in multi-word names
        oddunName.split(' ').some(word => word.includes(searchTerm)) ||
        traditionalSubtitle.split(' ').some(word => word.includes(searchTerm))
      );
    });
    
    setFilteredOdduns(filtered);
    // Reset to first result if searching
    if (filtered.length > 0) {
      const firstResultIndex = odduns.findIndex(oddun => oddun.id === filtered[0].id);
      if (firstResultIndex !== -1) {
        setCurrentIndex(firstResultIndex);
      }
    }
  };

  // Navigate within filtered results for Encyclopedia mode
  const navigateFiltered = (direction) => {
    if (difficulty !== 'encyclopedia' || !searchQuery || filteredOdduns.length === 0) {
      return;
    }
    
    const currentOddun = odduns[currentIndex];
    const currentFilteredIndex = filteredOdduns.findIndex(o => o.id === currentOddun.id);
    
    let nextFilteredIndex;
    if (direction === 'next') {
      nextFilteredIndex = Math.min(filteredOdduns.length - 1, currentFilteredIndex + 1);
    } else {
      nextFilteredIndex = Math.max(0, currentFilteredIndex - 1);
    }
    
    const nextOddun = filteredOdduns[nextFilteredIndex];
    const nextIndex = odduns.findIndex(o => o.id === nextOddun.id);
    
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    }
  };

  // Handle contribution submission
  const handleContributionSubmit = async () => {
    if (!contributionText.trim()) {
      toast.error('Por favor escribe tu contribución antes de enviar.');
      return;
    }

    try {
      const response = await axios.post(`${API}/ifa-signs/contribute`, {
        oddun_id: odduns[currentIndex]?.id,
        section: contributionSection,
        type: contributionType,
        content: contributionText,
        user_id: user?.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('¡Contribución enviada exitosamente! Un administrador la revisará pronto. ¡Gracias por ayudar a mejorar el contenido!');
        setShowContributionModal(false);
        setContributionText('');
        setContributionSection('');
      }
    } catch (error) {
      console.error('Error submitting contribution:', error);
      toast.error('Error al enviar la contribución. Inténtalo de nuevo.');
    }
  };

  // Open contribution modal
  const openContributionModal = (section) => {
    setContributionSection(section);
    setShowContributionModal(true);
  };


  // Load user's cowrie balance
  useEffect(() => {
    if (user) {
      setCowrieBalance(user.cowrie_shells || 0);
    }
  }, [user]);

  // Initialize filtered odduns when odduns change
  useEffect(() => {
    setFilteredOdduns(odduns);
    setSearchQuery(''); // Reset search when switching levels
  }, [odduns]);

  // Reset game function
  const resetGame = () => {
    // Clear any existing timer
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
      setAutoAdvanceTimer(null);
    }
    
    // Reset all game state
    setGameState('menu');
    setSessionId(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setScore({ correct: 0, total: 0 });
    setTimeLeft(60);
    setSelectedAnswer('');
    setSelectedAnswers([]);
    setSelectedTopics([]);
    setSelectedOddunSet('meyi');
    setQuestionCount(10);
    setGameMode('multiple_choice');
    setGameResults(null);
    setShowResult(false);
    setLastAnswerCorrect(null);
    setWrongAnswers(0);
    setIsDisqualified(false);
    setCurrentQuestionData(null);
    setSearchQuery('');
    setFilteredOdduns([]);
    setOdduns([]);
    setCurrentIndex(0);
  };







  // Menu Screen
  if (gameState === 'menu') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/dashboard"
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Signos de Ifá</h1>
              <p className="text-emerald-600">Elige tu nivel de dificultad para estudiar los Odduns sagrados</p>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">¿Cómo quieres aprender?</h2>
              <p className="text-emerald-700 max-w-2xl mx-auto">
                Elige entre modo juego con preguntas y desafíos, o modo estudio para explorar libremente.
              </p>
            </div>
            
            {/* Game vs Study Mode */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-emerald-200 hover:border-emerald-400 transition-all cursor-pointer"
                   onClick={() => setGameState('difficulty')}>
                <div className="text-center">
                  <div className="text-6xl mb-4">🎮</div>
                  <h3 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">Modo Juego</h3>
                  <p className="text-emerald-600 mb-6">
                    Responde preguntas sobre Odduns con tiempo limitado, temas específicos y sistema de puntuación.
                  </p>
                  <div className="bg-emerald-100 rounded-lg p-4">
                    <div className="text-sm text-emerald-700">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Target className="w-4 h-4" />
                        <span className="font-semibold">9 Temas de Estudio</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span className="font-semibold">4 Niveles de Dificultad</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-amber-200 hover:border-amber-400 transition-all cursor-pointer"
                   onClick={() => handleDifficultySelect('encyclopedia')}>
                <div className="text-center">
                  <div className="text-6xl mb-4">📖</div>
                  <h3 className="font-cinzel text-2xl font-bold text-amber-800 mb-4">Modo Estudio</h3>
                  <p className="text-amber-600 mb-6">
                    Explora libremente todos los Odduns con búsqueda avanzada y contenido detallado.
                  </p>
                  <div className="bg-amber-100 rounded-lg p-4">
                    <div className="text-sm text-amber-700">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Search className="w-4 h-4" />
                        <span className="font-semibold">Búsqueda Avanzada</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <BookOpen className="w-4 h-4" />
                        <span className="font-semibold">Todos los Odduns</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Difficulty Selection Screen (for Game Mode)
  if (gameState === 'difficulty') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button 
              onClick={() => setGameState('menu')}
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Selecciona Dificultad</h1>
              <p className="text-emerald-600">Elige tu nivel para el juego de Signos de Ifá</p>
            </div>
          </div>

          {/* Difficulty Selection */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">Niveles de Juego</h2>
              <p className="text-emerald-700 max-w-2xl mx-auto">
                Cada nivel tiene diferentes límites de tiempo y errores permitidos.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {difficulties.map((diff, index) => (
                <div 
                  key={index} 
                  className={`game-card bg-gradient-to-br ${diff.color} p-8 rounded-2xl text-white cursor-pointer relative overflow-hidden hover:scale-[1.02] transition-all duration-300`}
                  onClick={() => handleDifficultySelect(diff.value)}
                >
                  <div className="text-6xl mb-4 text-center">{diff.icon}</div>
                  <h3 className="font-cinzel text-2xl font-bold mb-3 text-center">{diff.label}</h3>
                  <p className="text-white text-opacity-90 text-center leading-relaxed mb-4">
                    {diff.description}
                  </p>
                  
                  <div className="bg-white bg-opacity-20 rounded-lg p-3 mb-6">
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <BookOpen className="w-4 h-4" />
                      <span className="font-semibold">{diff.oddunCount} Odduns</span>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button 
                      className="bg-white text-emerald-800 px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-800"></div>
                      ) : (
                        <>
                          <Lightbulb className="w-5 h-5" />
                          Estudiar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Topics Selection Screen (simplified - just topics)
  if (gameState === 'topics') {
    const currentDifficulty = difficulties.find(d => d.value === difficulty);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button 
              onClick={() => setGameState('difficulty')}
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Selección de Temas</h1>
              <p className="text-emerald-600">
                Nivel: {currentDifficulty?.label} - Elige los temas que quieres estudiar
              </p>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Topics Selection */}
            <div className="mb-8">
              <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-2 text-center">Selecciona Temas</h2>
              <p className="text-emerald-600 text-center mb-6">
                Puedes elegir uno o varios temas para tu sesión de estudio
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                {gameTopics.map((topic) => (
                  <div
                    key={topic.id}
                    className={`cursor-pointer p-6 rounded-xl border-2 transition-all duration-300 ${
                      selectedTopics.includes(topic.id)
                        ? 'border-emerald-500 bg-emerald-50 shadow-lg scale-105'
                        : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
                    }`}
                    onClick={() => handleTopicSelection(topic.id)}
                  >
                    <div className="text-4xl mb-3 text-center">{topic.icon}</div>
                    <h3 className="font-bold text-lg text-emerald-800 text-center mb-2">{topic.name}</h3>
                    <p className="text-emerald-600 text-sm text-center mb-3">{topic.description}</p>
                    <div className={`text-xs px-2 py-1 rounded-full text-center ${
                      topic.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                      topic.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {topic.difficulty === 'easy' ? 'Fácil' : 
                       topic.difficulty === 'medium' ? 'Medio' : 'Difícil'}
                    </div>
                    
                    {selectedTopics.includes(topic.id) && (
                      <div className="mt-3 text-center">
                        <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Aleatorio Option */}
              <div className="flex justify-center">
                <div
                  className={`cursor-pointer p-6 rounded-xl border-2 transition-all duration-300 ${
                    selectedTopics.length === gameTopics.length
                      ? 'border-emerald-500 bg-emerald-50 shadow-lg scale-105'
                      : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
                  }`}
                  onClick={() => {
                    if (selectedTopics.length === gameTopics.length) {
                      setSelectedTopics([]);
                    } else {
                      setSelectedTopics(gameTopics.map(t => t.id));
                    }
                  }}
                >
                  <div className="text-4xl mb-3 text-center">🎲</div>
                  <h3 className="font-bold text-lg text-emerald-800 text-center mb-2">Aleatorio</h3>
                  <p className="text-emerald-600 text-sm text-center mb-3">Combinación aleatoria (mayor dificultad)</p>
                  <div className="text-xs px-2 py-1 rounded-full text-center bg-purple-100 text-purple-700">
                    Experto
                  </div>
                  
                  {selectedTopics.length === gameTopics.length && (
                    <div className="mt-3 text-center">
                      <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto" />
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Topics Summary */}
              <div className="text-center mt-4">
                <div className="text-emerald-700">
                  <strong>Temas seleccionados:</strong> {
                    selectedTopics.length === 0 ? 'Ninguno' :
                    selectedTopics.length === gameTopics.length ? 'Aleatorio (Todos los temas)' :
                    selectedTopics.map(id => gameTopics.find(t => t.id === id)?.name).join(', ')
                  }
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <div className="text-center">
              <button
                onClick={() => setGameState('odduns')}
                disabled={selectedTopics.length === 0}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-12 py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto shadow-xl"
              >
                <ChevronRight className="w-6 h-6" />
                {selectedTopics.length === 0 ? 'Selecciona al menos un tema' : 'Continuar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Oddun Selection Screen
  if (gameState === 'odduns') {
    const currentDifficulty = difficulties.find(d => d.value === difficulty);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button 
              onClick={() => setGameState('topics')}
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Selección de Odduns</h1>
              <p className="text-emerald-600">
                Nivel: {currentDifficulty?.label} - Elige qué Odduns incluir en el juego
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">¿Qué Odduns quieres incluir?</h2>
              <p className="text-emerald-700 max-w-2xl mx-auto">
                Selecciona el conjunto de Odduns que aparecerán en las preguntas del juego.
              </p>
            </div>

            {/* Oddun Selection Options */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {oddunSelectionOptions.map((option) => (
                <div
                  key={option.id}
                  className={`cursor-pointer p-6 rounded-xl border-2 transition-all duration-300 ${
                    selectedOddunSet === option.id
                      ? 'border-emerald-500 bg-emerald-50 shadow-lg scale-105'
                      : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
                  }`}
                  onClick={() => setSelectedOddunSet(option.id)}
                >
                  <div className="text-center">
                    <div className="text-4xl mb-3">
                      {option.id === 'meyi' ? '🔢' : 
                       option.id === 'sequential' ? '📈' :
                       option.id === 'random' ? '🎲' : '✨'}
                    </div>
                    <h3 className="font-bold text-lg text-emerald-800 mb-2">{option.name}</h3>
                    <p className="text-emerald-600 text-sm leading-relaxed">
                      {option.description}
                    </p>
                    
                    {selectedOddunSet === option.id && (
                      <div className="mt-3">
                        <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Selection Summary */}
            <div className="bg-white rounded-2xl p-6 shadow-xl mb-8">
              <h3 className="font-cinzel text-lg font-bold text-emerald-800 mb-4 text-center">Selección Actual</h3>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600 mb-2">
                  {oddunSelectionOptions.find(o => o.id === selectedOddunSet)?.name}
                </div>
                <div className="text-gray-600">
                  {oddunSelectionOptions.find(o => o.id === selectedOddunSet)?.description}
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <div className="text-center">
              <button
                onClick={() => {
                  if (selectedOddunSet === 'custom') {
                    setGameState('custom_odduns');
                  } else {
                    setGameState('question_config');
                  }
                }}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-12 py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all duration-300 flex items-center gap-3 mx-auto shadow-xl"
              >
                <ChevronRight className="w-6 h-6" />
                Continuar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Custom Oddun Selection Screen (when user selects "Selección Personalizada")
  if (gameState === 'custom_odduns') {
    const currentDifficulty = difficulties.find(d => d.value === difficulty);
    
    // Mock oddun data for the custom selection (in real app, this would come from API)
    const availableOdduns = [
      { id: 1, name: 'Baba Ejiogbe', binary_pattern: '11111111', position: 1 },
      { id: 2, name: 'Oyeku Meji', binary_pattern: '00000000', position: 2 },
      { id: 3, name: 'Iwori Meji', binary_pattern: '01010101', position: 3 },
      { id: 4, name: 'Odi Meji', binary_pattern: '10101010', position: 4 },
      { id: 5, name: 'Iroso Meji', binary_pattern: '11001100', position: 5 },
      { id: 6, name: 'Ojuani Meji', binary_pattern: '00110011', position: 6 },
      { id: 7, name: 'Obara Meji', binary_pattern: '11110000', position: 7 },
      { id: 8, name: 'Okanran Meji', binary_pattern: '00001111', position: 8 },
      // Add more odduns as needed...
    ];
    
    const handleOddunToggle = (oddunId) => {
      if (selectedOdduns.includes(oddunId)) {
        setSelectedOdduns(prev => prev.filter(id => id !== oddunId));
      } else {
        setSelectedOdduns(prev => [...prev, oddunId]);
      }
    };
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button 
              onClick={() => setGameState('odduns')}
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Selección Personalizada de Odduns</h1>
              <p className="text-emerald-600">
                Elige individualmente los Odduns que quieres incluir en el juego
              </p>
            </div>
          </div>

          {/* Counter */}
          <div className="text-center mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-xl max-w-md mx-auto">
              <h3 className="font-cinzel text-lg font-bold text-emerald-800 mb-2">Odduns Seleccionados</h3>
              <div className="text-4xl font-bold text-emerald-600 mb-2">{selectedOdduns.length}</div>
              <div className="text-gray-600">de {availableOdduns.length} disponibles</div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Oddun Grid */}
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {availableOdduns.map((oddun) => (
                <div
                  key={oddun.id}
                  className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                    selectedOdduns.includes(oddun.id)
                      ? 'bg-emerald-100 border-emerald-400 shadow-lg scale-105'
                      : 'bg-white border-gray-200 hover:border-emerald-300 hover:shadow-md'
                  }`}
                  onClick={() => handleOddunToggle(oddun.id)}
                >
                  <div className="text-center">
                    {/* Position number */}
                    <div className="text-sm font-bold text-emerald-600 mb-2">#{oddun.position}</div>
                    
                    {/* Oddun name */}
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">{oddun.name}</h3>
                    
                    {/* Binary pattern display */}
                    <div className="bg-gray-100 p-2 rounded-lg mb-3">
                      <div className="text-xs text-gray-600 mb-1">Patrón Binario</div>
                      <div className="flex justify-center gap-1">
                        {/* Display pattern as two columns using picture1 and picture2 */}
                        <div className="space-y-1">
                          {oddun.binary_pattern.slice(4, 8).split('').map((bit, i) => (
                            <div key={i} className="flex justify-center">
                              <img 
                                src={bit === '1' ? '/picture1.png' : '/picture2.jpeg'} 
                                alt={bit === '1' ? 'Línea Simple' : 'Línea Doble'}
                                className="w-8 h-4 object-contain"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="space-y-1">
                          {oddun.binary_pattern.slice(0, 4).split('').map((bit, i) => (
                            <div key={i} className="flex justify-center">
                              <img 
                                src={bit === '1' ? '/picture1.png' : '/picture2.jpeg'} 
                                alt={bit === '1' ? 'Línea Simple' : 'Línea Doble'}
                                className="w-8 h-4 object-contain"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Selection indicator */}
                    {selectedOdduns.includes(oddun.id) && (
                      <div className="mt-2">
                        <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 mb-8">
              <button
                onClick={() => setSelectedOdduns([])}
                className="bg-gray-500 text-white px-6 py-2 rounded-xl font-semibold hover:bg-gray-600 transition-colors"
              >
                Limpiar Todo
              </button>
              <button
                onClick={() => setSelectedOdduns(availableOdduns.map(o => o.id))}
                className="bg-emerald-500 text-white px-6 py-2 rounded-xl font-semibold hover:bg-emerald-600 transition-colors"
              >
                Seleccionar Todo
              </button>
            </div>

            {/* Continue Button */}
            <div className="text-center">
              <button
                onClick={() => setGameState('question_config')}
                disabled={selectedOdduns.length === 0}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-12 py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto shadow-xl"
              >
                <ChevronRight className="w-6 h-6" />
                {selectedOdduns.length === 0 ? 'Selecciona al menos un Oddun' : 'Continuar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question Configuration Screen (Type and Count)
  if (gameState === 'question_config') {
    const currentDifficulty = difficulties.find(d => d.value === difficulty);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button 
              onClick={() => {
                if (selectedOddunSet === 'custom') {
                  setGameState('custom_odduns');
                } else {
                  setGameState('odduns');
                }
              }}
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Tipo de Pregunta y Número</h1>
              <p className="text-emerald-600">
                Configuración final del juego
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-4">Configuración Final</h2>
              <p className="text-emerald-700 max-w-2xl mx-auto">
                Define el tipo de preguntas y cuántas quieres responder.
              </p>
            </div>

            {/* Game Configuration */}
            <div className="space-y-8 mb-8">
              {/* Question Types */}
              <div>
                <h3 className="font-cinzel text-2xl font-bold text-emerald-800 mb-6 text-center">
                  Selecciona el Modo de Juego
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {gameModes.map((mode) => (
                    <div
                      key={mode.id}
                      className={`cursor-pointer p-6 rounded-xl border-2 transition-all duration-300 ${
                        gameMode === mode.id
                          ? 'border-emerald-500 bg-emerald-50 shadow-lg scale-105'
                          : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
                      }`}
                      onClick={() => setGameMode(mode.id)}
                    >
                      <div className="text-center">
                        <div className="text-4xl mb-3">{mode.icon}</div>
                        <h3 className="font-bold text-lg text-emerald-800 mb-2">{mode.name}</h3>
                        <p className="text-emerald-600 text-sm leading-relaxed">
                          {mode.description}
                        </p>
                        
                        {gameMode === mode.id && (
                          <div className="mt-3">
                            <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="text-center mt-6">
                  <div className="text-emerald-700">
                    <strong>Modo seleccionado:</strong> {gameModes.find(m => m.id === gameMode)?.name || 'Ninguno'}
                  </div>
                </div>
              </div>

              {/* Question Count */}
              <div>
                <h3 className="font-cinzel text-2xl font-bold text-emerald-800 mb-6 text-center">
                  Número de Preguntas
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                  {questionCounts.map((count) => (
                    <button
                      key={count}
                      onClick={() => setQuestionCount(count)}
                      className={`py-3 px-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                        questionCount === count
                          ? 'bg-emerald-500 text-white shadow-lg scale-105'
                          : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-emerald-300 hover:shadow-md'
                      }`}
                    >
                      {count}
                    </button>
                  ))}
                </div>
                
                <div className="text-center mt-6">
                  <div className="text-emerald-700">
                    <strong>Preguntas seleccionadas:</strong> {questionCount}
                  </div>
                </div>
              </div>
            </div>

            {/* Game Summary */}
            <div className="bg-white rounded-2xl p-6 shadow-xl mb-8">
              <h3 className="font-cinzel text-lg font-bold text-emerald-800 mb-4 text-center">Resumen del Juego</h3>
              <div className="grid md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-emerald-600">{selectedTopics.length}</div>
                  <div className="text-sm text-gray-600">Temas</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">
                    {selectedOddunSet === 'custom' ? selectedOdduns.length : 
                     oddunSelectionOptions.find(o => o.id === selectedOddunSet)?.name || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600">Odduns</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">{questionCount}</div>
                  <div className="text-sm text-gray-600">Preguntas</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-600">
                    {gameModes.find(m => m.id === gameMode)?.icon || '🔤'}
                  </div>
                  <div className="text-sm text-gray-600">Tipo</div>
                </div>
              </div>
            </div>

            {/* Start Game Button */}
            <div className="text-center">
              <button
                onClick={startGame}
                disabled={loading}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-12 py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto shadow-xl"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                ) : (
                  <Play className="w-6 h-6" />
                )}
                Comenzar Juego
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && odduns.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800 mx-auto mb-4"></div>
          <p className="text-blue-800">Cargando los Signos de Ifá...</p>
        </div>
      </div>
    );
  }

  // Study Screen (Encyclopedia Mode)
  if (gameState === 'study') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-700">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={resetGame}
              className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-white">Signos de Ifá</h1>
              <p className="text-emerald-200">
                Nivel: <span className="font-bold capitalize">
                  {difficulties.find(d => d.value === difficulty)?.label || 'Principiante'}
                </span> - {difficulties.find(d => d.value === difficulty)?.oddunCount || 16} Odduns
              </p>
            </div>
          </div>
          
          {/* Cowrie Balance */}
          <div className="bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
            <Coins className="w-6 h-6 text-yellow-600" />
            <div>
              <div className="text-2xl font-bold text-yellow-600">{cowrieBalance}</div>
              <div className="text-xs text-gray-600">Conchas de Cowrie</div>
            </div>
          </div>
        </div>

        {/* Search Bar - Only for Encyclopedia Mode */}
        {difficulty === 'encyclopedia' && (
          <div className="mb-8">
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o número de Oddun..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white rounded-xl shadow-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                />
              </div>
              {searchQuery && (
                <div className="mt-2 text-center">
                  <span className="text-emerald-200 text-sm">
                    {filteredOdduns.length} Oddun{filteredOdduns.length !== 1 ? 's' : ''} encontrado{filteredOdduns.length !== 1 ? 's' : ''}
                  </span>
                  {filteredOdduns.length > 0 && (
                    <button
                      onClick={() => handleSearch('')}
                      className="ml-2 text-emerald-300 hover:text-white text-sm underline"
                    >
                      Limpiar búsqueda
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

          {/* Progress Bar */}
          {difficulty !== 'encyclopedia' && (
            <div className="mb-8">
              <div className="flex justify-between text-emerald-200 text-sm mb-2">
                <span>Oddun {currentIndex + 1} de {odduns.length}</span>
                <span>{Math.round(((currentIndex + 1) / odduns.length) * 100)}% Completado</span>
              </div>
              <div className="progress-bar bg-emerald-800">
                <div 
                  className="progress-fill bg-gradient-to-r from-yellow-400 to-emerald-400" 
                  style={{ width: `${((currentIndex + 1) / odduns.length) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
          
          {/* Encyclopedia Mode Info */}
          {difficulty === 'encyclopedia' && (
            <div className="mb-8 text-center">
              <div className="text-emerald-200 text-sm">
                {searchQuery ? (
                  <span>
                    Mostrando Oddun {odduns[currentIndex]?.position || currentIndex + 1} - 
                    {filteredOdduns.length > 0 && ` ${filteredOdduns.findIndex(o => o.id === odduns[currentIndex]?.id) + 1} de ${filteredOdduns.length} resultados`}
                  </span>
                ) : (
                  <span>Oddun {odduns[currentIndex]?.position || currentIndex + 1} de {odduns.length} - Modo Referencia</span>
                )}
              </div>
            </div>
          )}

          {/* Navigation Controls - Enhanced for Mobile */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 mb-8 px-4">
            <button
              onClick={() => {
                if (difficulty === 'encyclopedia' && searchQuery && filteredOdduns.length > 0) {
                  navigateFiltered('prev');
                } else {
                  handleOddunChange(Math.max(0, currentIndex - 1));
                }
              }}
              disabled={
                difficulty === 'encyclopedia' && searchQuery 
                  ? (filteredOdduns.length === 0 || filteredOdduns.findIndex(o => o.id === odduns[currentIndex]?.id) === 0 || loadingDetails)
                  : (currentIndex === 0 || loadingDetails)
              }
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-white bg-opacity-20 text-white rounded-xl font-semibold hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">
                {difficulty === 'encyclopedia' && searchQuery ? 'Resultado Anterior' : 'Anterior'}
              </span>
            </button>
            
            <div className="px-4 sm:px-6 py-3 bg-white bg-opacity-20 text-white rounded-xl font-semibold text-center">
              {difficulty === 'encyclopedia' && searchQuery ? (
                <span className="text-sm sm:text-base">
                  {filteredOdduns.length > 0 
                    ? `${filteredOdduns.findIndex(o => o.id === odduns[currentIndex]?.id) + 1} / ${filteredOdduns.length}`
                    : 'Sin resultados'
                  }
                </span>
              ) : (
                <span className="text-sm sm:text-base">{currentIndex + 1} / {odduns.length}</span>
              )}
              {loadingDetails && <div className="text-xs mt-1">Cargando...</div>}
            </div>
            
            <button
              onClick={() => {
                if (difficulty === 'encyclopedia' && searchQuery && filteredOdduns.length > 0) {
                  navigateFiltered('next');
                } else {
                  handleOddunChange(Math.min(odduns.length - 1, currentIndex + 1));
                }
              }}
              disabled={
                difficulty === 'encyclopedia' && searchQuery 
                  ? (filteredOdduns.length === 0 || filteredOdduns.findIndex(o => o.id === odduns[currentIndex]?.id) === filteredOdduns.length - 1 || loadingDetails)
                  : (currentIndex === odduns.length - 1 || loadingDetails)
              }
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-white bg-opacity-20 text-white rounded-xl font-semibold hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-sm sm:text-base">
                {difficulty === 'encyclopedia' && searchQuery ? 'Resultado Siguiente' : 'Siguiente'}
              </span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Oddun Study Card */}
          {odduns.length > 0 && odduns[currentIndex] && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-2xl mb-6 relative">
                {/* Data Source Badge for Overall Oddun - Admin Only */}
                {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'información general')}
                
                {/* Visual Header */}
                <div className="text-center mb-8">
                  <div className="text-8xl mb-4">🔮</div>
                  <h2 className="font-cinzel text-3xl font-bold text-emerald-800 mb-2">
                    {odduns[currentIndex].name}
                    {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'nombre', 'inline')}
                  </h2>
                  <h3 className="text-xl font-medium text-emerald-700 text-opacity-90 mb-4 text-center italic">
                    {(currentOddunDetails || odduns[currentIndex])?.traditional_name || 
                     (currentOddunDetails || odduns[currentIndex])?.alternative_names?.[0] ||
                     `${odduns[currentIndex].name} Meji`}
                  </h3>
                  <div className="flex justify-center mb-4">
                    <span className="bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold">
                      {odduns[currentIndex].title || 'Oddun Principal'}
                    </span>
                  </div>
                  
                  {/* Oddun Symbol Display - Enhanced for Mobile */}
                  <div className="mb-6 text-center">
                    <div className="bg-gray-100 p-4 sm:p-6 rounded-xl inline-block w-full max-w-sm mx-auto relative">
                      <h4 className="text-sm text-gray-600 mb-3 sm:mb-4 font-semibold">
                        Signo Oddun
                        {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'patrón binario', 'inline')}
                      </h4>
                      <div className="flex justify-center gap-8 sm:gap-12">
                        {/* Display Pattern as two columns using picture1 and picture2 */}
                        {/* LEFT COLUMN - Second row of numbers (slice 4,8) */}
                        <div className="text-center">
                          <div className="space-y-2 sm:space-y-3">
                            {odduns[currentIndex].binary_pattern ? 
                              odduns[currentIndex].binary_pattern.slice(4, 8).split('').map((bit, i) => (
                                <div key={i} className="flex justify-center">
                                  <img 
                                    src={bit === '1' ? '/picture1.png' : '/picture2.jpeg'} 
                                    alt={bit === '1' ? 'Línea Simple' : 'Línea Doble'}
                                    className="w-16 h-8 sm:w-20 sm:h-12 object-contain"
                                    title={bit === '1' ? 'Línea Simple (1)' : 'Línea Doble (0)'}
                                  />
                                </div>
                              )) : 
                              // Default pattern if no binary_pattern
                              ['1','1','1','1'].map((bit, i) => (
                                <div key={i} className="flex justify-center">
                                  <img 
                                    src='/picture1.png'
                                    alt='Línea Simple'
                                    className="w-16 h-8 sm:w-20 sm:h-12 object-contain"
                                    title='Línea Simple (1)'
                                  />
                                </div>
                              ))
                            }
                          </div>
                        </div>
                        {/* RIGHT COLUMN - First row of numbers (slice 0,4) */}
                        <div className="text-center">
                          <div className="space-y-2 sm:space-y-3">
                            {odduns[currentIndex].binary_pattern ? 
                              odduns[currentIndex].binary_pattern.slice(0, 4).split('').map((bit, i) => (
                                <div key={i} className="flex justify-center">
                                  <img 
                                    src={bit === '1' ? '/picture1.png' : '/picture2.jpeg'} 
                                    alt={bit === '1' ? 'Línea Simple' : 'Línea Doble'}
                                    className="w-16 h-8 sm:w-20 sm:h-12 object-contain"
                                    title={bit === '1' ? 'Línea Simple (1)' : 'Línea Doble (0)'}
                                  />
                                </div>
                              )) : 
                              // Default pattern if no binary_pattern
                              ['1','1','1','1'].map((bit, i) => (
                                <div key={i} className="flex justify-center">
                                  <img 
                                    src='/picture1.png'
                                    alt='Línea Simple'
                                    className="w-16 h-8 sm:w-20 sm:h-12 object-contain"
                                    title='Línea Simple (1)'
                                  />
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reorganized content in requested order */}
                <div className="space-y-8 mb-10">
                  
                  {/* Data Source Information Panel - Admin Only */}
                  {user?.role === 'admin' && (
                    <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                        🔍 Información de Fuentes de Datos (Solo Admin)
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-semibold text-blue-700 mb-2">Fuente Principal</h5>
                          <div className="flex items-center gap-2">
                            {renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'principal', 'inline')}
                            <span className="text-sm text-blue-600">
                              {getDataSourceInfo(currentOddunDetails || odduns[currentIndex]).description}
                            </span>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-semibold text-blue-700 mb-2">Completitud de Datos</h5>
                          <div className="flex items-center gap-2">
                            <div className="bg-white px-3 py-1 rounded-full text-sm font-medium">
                              {(currentOddunDetails || odduns[currentIndex])?.completion_percentage || 0}% completo
                            </div>
                            {((currentOddunDetails || odduns[currentIndex])?.has_data) && (
                              <span className="text-xs text-green-600">✅ Datos ricos disponibles</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 text-xs text-blue-600">
                        💡 Las etiquetas de fuente en cada sección indican el origen específico de esa información.
                      </div>
                    </div>
                  )}
                
                  {/* 0. El secreto del oddun - Premium Content - Mobile Enhanced */}
                  <div className="p-4 sm:p-6 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl border-2 border-purple-300">
                    <h4 className="font-bold text-purple-800 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                      <span className="flex items-center gap-2">
                        🔮 El Secreto del Oddun
                      </span>
                      <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                        PREMIUM
                      </span>
                    </h4>
                    
                    {user?.purchased_secrets?.includes(odduns[currentIndex]?.id) ? (
                      // Show secret content if purchased
                      <div className="text-purple-700 leading-relaxed">
                        <p className="font-medium mb-3 text-sm sm:text-base">
                          {(currentOddunDetails || odduns[currentIndex])?.secret_content || 
                           `El secreto profundo de ${odduns[currentIndex]?.name}: Este Oddun guarda misterios ancestrales que solo se revelan a quienes han demostrado dedicación en el camino de Ifá. Los secretos incluyen trabajos especiales, invocaciones poderosas y conocimientos que trascienden lo común.`}
                        </p>
                        <div className="bg-purple-50 p-3 sm:p-4 rounded-lg border-l-4 border-purple-400 mt-4">
                          <p className="text-xs sm:text-sm italic text-purple-600">
                            "Los secretos del Oddun son para quienes buscan la sabiduría más profunda..."
                          </p>
                        </div>
                      </div>
                    ) : (
                      // Show purchase option if not bought - Mobile Enhanced
                      <div className="text-center">
                        <div className="bg-white p-4 sm:p-6 rounded-lg border-2 border-dashed border-purple-300 mb-4">
                          <div className="text-3xl sm:text-4xl mb-3">🔒</div>
                          <h5 className="font-bold text-purple-800 mb-2 text-sm sm:text-base">Contenido Secreto Bloqueado</h5>
                          <p className="text-purple-600 text-xs sm:text-sm mb-4 px-2 leading-relaxed">
                            Descubre los secretos más profundos y trabajos especiales de este Oddun.
                            <br />
                            Conocimiento reservado para estudiantes dedicados.
                          </p>
                          <div className="flex items-center justify-center gap-2 mb-4">
                            <span className="text-xl sm:text-2xl">🐚</span>
                            <span className="font-bold text-purple-800 text-sm sm:text-base">100 Conchas de Cowrie</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handlePurchaseSecret(odduns[currentIndex]?.id)}
                          disabled={cowrieBalance < 100}
                          className={`w-full px-4 sm:px-6 py-3 rounded-xl font-bold transition-all text-sm sm:text-base ${
                            cowrieBalance >= 100
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {cowrieBalance >= 100 ? 'Desbloquear Secreto' : 'Conchas Insuficientes'}
                        </button>
                        <p className="text-xs text-gray-600 mt-2">
                          Tu saldo: {cowrieBalance} conchas
                        </p>
                      </div>
                    )}
                  </div>
                
                  {/* Rezo (Original) */}
                  {(((currentOddunDetails || odduns[currentIndex])?.rezo) || 
                    ((currentOddunDetails || odduns[currentIndex])?.rezo_principal)) && (
                    <div className="p-6 bg-yellow-50 rounded-xl border-l-4 border-yellow-400 relative">
                      <h4 className="font-bold text-yellow-800 mb-4 flex items-center gap-2">
                        🙏 Rezo
                        {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'rezo', 'inline')}
                        {loadingDetails && <div className="text-xs text-yellow-600">(Cargando...)</div>}
                      </h4>
                      <p className="text-yellow-700 leading-relaxed italic font-medium whitespace-pre-wrap">
                        {(currentOddunDetails || odduns[currentIndex]).rezo || 
                         (currentOddunDetails || odduns[currentIndex]).rezo_principal || 
                         'Rezo tradicional no disponible'}
                      </p>
                      
                      {/* Additional Rezos if available */}
                      {(currentOddunDetails || odduns[currentIndex])?.rezos_adicionales && 
                       Array.isArray((currentOddunDetails || odduns[currentIndex]).rezos_adicionales) && 
                       (currentOddunDetails || odduns[currentIndex]).rezos_adicionales.length > 1 && (
                        <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
                          <h6 className="font-semibold text-yellow-800 mb-2 text-sm">Rezos Adicionales:</h6>
                          <div className="space-y-2">
                            {(currentOddunDetails || odduns[currentIndex]).rezos_adicionales.slice(1).map((rezo, idx) => (
                              <p key={idx} className="text-yellow-700 text-sm italic leading-relaxed">
                                • {rezo}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Contribution Button */}
                      <div className="mt-4 pt-4 border-t border-yellow-200">
                        <button
                          onClick={() => openContributionModal('Rezo')}
                          className="flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
                        >
                          ✨ Contribuir a esta sección
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Rezo Personalizado */}
                  {((currentOddunDetails || odduns[currentIndex])?.rezo_personalizado) && (
                    <div className="p-6 bg-emerald-50 rounded-xl border-l-4 border-emerald-400 relative">
                      <h4 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                        🔮 Rezo Personalizado
                        {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'rezo_personalizado', 'inline')}
                        {loadingDetails && <div className="text-xs text-emerald-600">(Cargando...)</div>}
                      </h4>
                      <p className="text-emerald-700 leading-relaxed italic font-medium whitespace-pre-wrap">
                        {(currentOddunDetails || odduns[currentIndex]).rezo_personalizado}
                      </p>
                      
                      {/* Contribution Button */}
                      <div className="mt-4 pt-4 border-t border-emerald-200">
                        <button
                          onClick={() => openContributionModal('Rezo Personalizado')}
                          className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm font-medium"
                        >
                          ✨ Contribuir a esta sección
                        </button>
                      </div>
                    </div>
                  )}
                
                  {/* 1. Suyere */}
                  {((currentOddunDetails || odduns[currentIndex])?.suyere) && (
                    <div className="p-6 bg-purple-50 rounded-xl border-l-4 border-purple-400 relative">
                      <h4 className="font-bold text-purple-800 mb-4 flex items-center gap-2">
                        🎵 Suyere
                        {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'suyere', 'inline')}
                        {loadingDetails && <div className="text-xs text-purple-600">(Cargando...)</div>}
                      </h4>
                      <p className="text-purple-700 leading-relaxed italic font-medium">
                        {(currentOddunDetails || odduns[currentIndex]).suyere}
                      </p>
                      
                      {/* Contribution Button */}
                      <div className="mt-4 pt-4 border-t border-purple-200">
                        <button
                          onClick={() => openContributionModal('Suyere')}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                        >
                          ✨ Contribuir a esta sección
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 2. Significado */}
                  <div className="p-6 bg-blue-50 rounded-xl border-l-4 border-blue-400 relative">
                    <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                      📖 Significado
                      {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'significado', 'inline')}
                      {loadingDetails && <div className="text-xs text-blue-600">(Cargando...)</div>}
                    </h4>
                    <p className="text-blue-700 leading-relaxed">
                      {(currentOddunDetails || odduns[currentIndex])?.meaning || 'Significado tradicional del Oddun'}
                    </p>
                    
                    {/* Contribution Button */}
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <button
                        onClick={() => openContributionModal('Significado')}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                      >
                        ✨ Contribuir a esta sección
                      </button>
                    </div>
                  </div>
                  
                  {/* 3. Descripción Tradicional Extendida */}
                  {(currentOddunDetails?.extended_description) && (
                    <div className="p-6 bg-cyan-50 rounded-xl border-l-4 border-cyan-400 relative">
                      <h4 className="font-bold text-cyan-800 mb-4 flex items-center gap-2">
                        📜 Descripción Tradicional Extendida
                        {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'descripción_extendida', 'inline')}
                        {loadingDetails && <div className="text-xs text-cyan-600">(Cargando...)</div>}
                      </h4>
                      <p className="text-cyan-700 leading-relaxed">
                        {currentOddunDetails.extended_description}
                      </p>
                      
                      {/* Contribution Button */}
                      <div className="mt-4 pt-4 border-t border-cyan-200">
                        <button
                          onClick={() => openContributionModal('Descripción Tradicional Extendida')}
                          className="flex items-center gap-2 px-4 py-2 bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200 transition-colors text-sm font-medium"
                        >
                          ✨ Contribuir a esta sección
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 4. Nace en este Signo */}
                  <div className="p-6 bg-orange-50 rounded-xl border-l-4 border-orange-400 relative">
                    <h4 className="font-bold text-orange-800 mb-4 flex items-center gap-2">
                      🌱 Nace en este Signo
                      {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'nace_en_este_signo', 'inline')}
                      {loadingDetails && <div className="text-xs text-orange-600">(Cargando...)</div>}
                    </h4>
                    <ul className="text-orange-700 text-sm space-y-3">
                      {((currentOddunDetails || odduns[currentIndex])?.nace_en_este_signo || [
                        'La sabiduría ancestral',
                        'El equilibrio espiritual',
                        'La conexión divina'
                      ]).map((nace, i) => (
                        <li key={i} className="leading-relaxed">• {nace}</li>
                      ))}
                    </ul>
                    {(currentOddunDetails?.nace_en_este_signo?.length || 0) > 3 && (
                      <div className="text-xs text-orange-600 mt-4 font-semibold">
                        Total: {currentOddunDetails.nace_en_este_signo.length} elementos tradicionales
                      </div>
                    )}
                    
                    {/* Contribution Button */}
                    <div className="mt-4 pt-4 border-t border-orange-200">
                      <button
                        onClick={() => openContributionModal('Nace en este Signo')}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
                      >
                        ✨ Contribuir a esta sección
                      </button>
                    </div>
                  </div>
                  
                  {/* 5. Consejos Espirituales */}
                  <div className="p-6 bg-green-50 rounded-xl border-l-4 border-green-400 relative">
                    <h4 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                      💡 Consejos Espirituales
                      {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'consejos_espirituales', 'inline')}
                      {loadingDetails && <div className="text-xs text-green-600">(Cargando...)</div>}
                    </h4>
                    <ul className="text-green-700 text-sm space-y-3">
                      {((currentOddunDetails || odduns[currentIndex])?.consejos_espirituales || [
                        'Mantén la fe en los designios del destino',
                        'Respeta las tradiciones ancestrales',
                        'Busca la sabiduría en la naturaleza'
                      ]).map((consejo, i) => (
                        <li key={i} className="leading-relaxed">• {consejo}</li>
                      ))}
                    </ul>
                    {(currentOddunDetails?.consejos_espirituales?.length || 0) > 3 && (
                      <div className="text-xs text-green-600 mt-4 font-semibold">
                        Total: {currentOddunDetails.consejos_espirituales.length} consejos tradicionales
                      </div>
                    )}
                    
                    {/* Contribution Button */}
                    <div className="mt-4 pt-4 border-t border-green-200">
                      <button
                        onClick={() => openContributionModal('Consejos Espirituales')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                      >
                        ✨ Contribuir a esta sección
                      </button>
                    </div>
                  </div>
                  
                  {/* 6. Ofrendas */}
                  <div className="p-6 bg-indigo-50 rounded-xl border-l-4 border-indigo-400 relative">
                    <h4 className="font-bold text-indigo-800 mb-4 flex items-center gap-2">
                      ⭐ Ofrendas
                      {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'ofrendas', 'inline')}
                      {loadingDetails && <div className="text-xs text-indigo-600">(Cargando...)</div>}
                    </h4>
                    <ul className="text-indigo-700 text-sm space-y-3">
                      {((currentOddunDetails || odduns[currentIndex])?.ofrendas || [
                        'Flores blancas',
                        'Frutas frescas',
                        'Agua limpia'
                      ]).map((ofrenda, i) => (
                        <li key={i} className="leading-relaxed">• {ofrenda}</li>
                      ))}
                    </ul>
                    {(currentOddunDetails?.ofrendas?.length || 0) > 3 && (
                      <div className="text-xs text-indigo-600 mt-4 font-semibold">
                        Total: {currentOddunDetails.ofrendas.length} ofrendas tradicionales
                      </div>
                    )}
                    
                    {/* Contribution Button */}
                    <div className="mt-4 pt-4 border-t border-indigo-200">
                      <button
                        onClick={() => openContributionModal('Ofrendas')}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm font-medium"
                      >
                        ✨ Contribuir a esta sección
                      </button>
                    </div>
                  </div>
                  
                  {/* 7. Plantas */}
                  {(currentOddunDetails?.plantas?.length > 0) && (
                    <div className="p-6 bg-emerald-50 rounded-xl border-l-4 border-emerald-400 relative">
                      <h4 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                        🌿 Plantas
                        {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'plantas', 'inline')}
                        {loadingDetails && <div className="text-xs text-emerald-600">(Cargando...)</div>}
                      </h4>
                      <ul className="text-emerald-700 text-sm space-y-3">
                        {currentOddunDetails.plantas.map((planta, i) => (
                          <li key={i} className="leading-relaxed">• {planta}</li>
                        ))}
                      </ul>
                      <div className="text-xs text-emerald-600 mt-4 font-semibold">
                        Total: {currentOddunDetails.plantas.length} plantas tradicionales
                      </div>
                      
                      {/* Contribution Button */}
                      <div className="mt-4 pt-4 border-t border-emerald-200">
                        <button
                          onClick={() => openContributionModal('Plantas')}
                          className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm font-medium"
                        >
                          ✨ Contribuir a esta sección
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 8. Refranes del Oddun */}
                  {((currentOddunDetails || odduns[currentIndex])?.refranes?.length > 0) && (
                    <div className="p-6 bg-pink-50 rounded-xl border-l-4 border-pink-400 relative">
                      <h4 className="font-bold text-pink-800 mb-4 flex items-center gap-2">
                        📚 Refranes del Oddun
                        {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'refranes', 'inline')}
                        {loadingDetails && <div className="text-xs text-pink-600">(Cargando...)</div>}
                      </h4>
                      <div className="space-y-3">
                        {((currentOddunDetails || odduns[currentIndex])?.refranes || []).map((refran, i) => (
                          <p key={i} className="text-pink-700 italic leading-relaxed">"{refran}"</p>
                        ))}
                      </div>
                      {(currentOddunDetails?.refranes?.length || 0) > 5 && (
                        <div className="text-xs text-pink-600 mt-4 font-semibold">
                          Total: {currentOddunDetails.refranes.length} refranes tradicionales
                        </div>
                      )}
                      
                      {/* Contribution Button */}
                      <div className="mt-4 pt-4 border-t border-pink-200">
                        <button
                          onClick={() => openContributionModal('Refranes del Oddun')}
                          className="flex items-center gap-2 px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors text-sm font-medium"
                        >
                          ✨ Contribuir a esta sección
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 9. Patakines */}
                  {(currentOddunDetails?.patakines?.length > 0) && (
                    <div className="p-6 bg-yellow-50 rounded-xl border-l-4 border-yellow-400 relative">
                      <h4 className="font-bold text-yellow-800 mb-4 flex items-center gap-2">
                        📖 Patakines
                        {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'patakines', 'inline')}
                        {loadingDetails && <div className="text-xs text-yellow-600">(Cargando...)</div>}
                      </h4>
                      <ul className="text-yellow-700 text-sm space-y-3">
                        {currentOddunDetails.patakines.map((pataki, i) => (
                          <li key={i} className="leading-relaxed">• {pataki}</li>
                        ))}
                      </ul>
                      <div className="text-xs text-yellow-600 mt-4 font-semibold">
                        Total: {currentOddunDetails.patakines.length} historias sagradas
                      </div>
                      
                      {/* Contribution Button */}
                      <div className="mt-4 pt-4 border-t border-yellow-200">
                        <button
                          onClick={() => openContributionModal('Patakines')}
                          className="flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
                        >
                          ✨ Contribuir a esta sección
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 10. Obras del Signo */}
                  {(currentOddunDetails?.obras_del_signo?.length > 0) && (
                    <div className="p-6 bg-violet-50 rounded-xl border-l-4 border-violet-400">
                      <h4 className="font-bold text-violet-800 mb-4 flex items-center gap-2">
                        ⚡ Obras del Signo
                        {loadingDetails && <div className="text-xs text-violet-600">(Cargando...)</div>}
                      </h4>
                      <ul className="text-violet-700 text-sm space-y-3">
                        {currentOddunDetails.obras_del_signo.map((obra, i) => (
                          <li key={i} className="leading-relaxed">• {obra}</li>
                        ))}
                      </ul>
                      <div className="text-xs text-violet-600 mt-4 font-semibold">
                        Total: {currentOddunDetails.obras_del_signo.length} trabajos rituales
                      </div>
                      
                      {/* Contribution Button */}
                      <div className="mt-4 pt-4 border-t border-violet-200">
                        <button
                          onClick={() => openContributionModal('Obras del Signo')}
                          className="flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200 transition-colors text-sm font-medium"
                        >
                          ✨ Contribuir a esta sección
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 11. Tabúes */}
                  <div className="p-6 bg-red-50 rounded-xl border-l-4 border-red-400 relative">
                    <h4 className="font-bold text-red-800 mb-4 flex items-center gap-2">
                      ❌ Tabúes
                      {user?.role === 'admin' && renderDataSourceBadge(currentOddunDetails || odduns[currentIndex], 'taboos', 'inline')}
                      {loadingDetails && <div className="text-xs text-red-600">(Cargando...)</div>}
                    </h4>
                    <ul className="text-red-700 text-sm space-y-3">
                      {((currentOddunDetails || odduns[currentIndex])?.taboos || [
                        'Evitar las acciones precipitadas',
                        'No faltar el respeto a los mayores',
                        'Mantener la palabra dada'
                      ]).map((taboo, i) => (
                        <li key={i} className="leading-relaxed">• {taboo}</li>
                      ))}
                    </ul>
                    {(currentOddunDetails?.taboos?.length || 0) > 3 && (
                      <div className="text-xs text-red-600 mt-4 font-semibold">
                        Total: {currentOddunDetails.taboos.length} tabúes tradicionales
                      </div>
                    )}
                    
                    {/* Contribution Button */}
                    <div className="mt-4 pt-4 border-t border-red-200">
                      <button
                        onClick={() => openContributionModal('Tabúes')}
                        className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                      >
                        ✨ Contribuir a esta sección
                      </button>
                    </div>
                  </div>
                  
                </div>





              </div>
            </div>
          )}
      </div>

      {/* Secret Ritual Modal */}
      {showSecretRitual && secretRitualData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <h2 className="font-cinzel text-3xl font-bold text-purple-800 mb-2">
                🔮 Ritual Secreto Revelado
              </h2>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">
                {secretRitualData.oddun_name} ({secretRitualData.yoruba_name})
              </h3>
              <p className="text-gray-600">Conocimiento ancestral profundo</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-lg text-purple-800 mb-3">Obras Secretas del Signo</h4>
                <div className="bg-purple-50 rounded-xl p-4 mb-6">
                  <ul className="list-disc list-inside space-y-2 text-purple-700">
                    {secretRitualData.secret_works?.map((work, idx) => (
                      <li key={idx}>{work}</li>
                    ))}
                  </ul>
                </div>

                <h4 className="font-bold text-lg text-purple-800 mb-3">Patakines Avanzados</h4>
                <div className="bg-blue-50 rounded-xl p-4">
                  <ul className="list-disc list-inside space-y-2 text-blue-700">
                    {secretRitualData.advanced_patakines?.map((pataki, idx) => (
                      <li key={idx}>{pataki}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-lg text-purple-800 mb-3">Secretos Espirituales</h4>
                <div className="space-y-4">
                  <div className="bg-green-50 rounded-xl p-4">
                    <h5 className="font-semibold text-green-800 mb-2">Oraciones Avanzadas</h5>
                    <p className="text-green-700 text-sm">{secretRitualData.spiritual_secrets?.advanced_prayers}</p>
                  </div>
                  
                  <div className="bg-orange-50 rounded-xl p-4">
                    <h5 className="font-semibold text-orange-800 mb-2">Ofrendas Secretas</h5>
                    <div className="flex flex-wrap gap-2">
                      {secretRitualData.spiritual_secrets?.secret_offerings?.map((offering, idx) => (
                        <span key={idx} className="px-2 py-1 bg-orange-200 text-orange-800 rounded-full text-xs">
                          {offering}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-xl p-4">
                    <h5 className="font-semibold text-red-800 mb-2">Tabúes Ocultos</h5>
                    <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                      {secretRitualData.spiritual_secrets?.hidden_taboos?.map((taboo, idx) => (
                        <li key={idx}>{taboo}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-l-4 border-purple-500">
              <h4 className="font-bold text-lg text-purple-800 mb-3">Instrucciones del Ritual</h4>
              <p className="text-purple-700 leading-relaxed">{secretRitualData.ritual_instructions}</p>
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => setShowSecretRitual(false)}
                className="bg-purple-500 text-white px-8 py-3 rounded-xl font-semibold hover:bg-purple-600 transition-colors"
              >
                Cerrar Ritual Secreto
              </button>
            </div>

            <div className="mt-4 text-center text-sm text-gray-600">
              <p>💰 Gastaste 10 Conchas de Cowrie para acceder a este conocimiento sagrado</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Contribution Modal */}
      {showContributionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-emerald-800">✨ Contribuir al Conocimiento</h3>
              <button
                onClick={() => setShowContributionModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-lg">
                <h4 className="font-bold text-emerald-800 mb-2">
                  Sección: {contributionSection}
                </h4>
                <p className="text-emerald-700 text-sm">
                  ¡Ayúdanos a mejorar el contenido! Tu contribución será revisada por nuestros administradores.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de contribución:
                </label>
                <select
                  value={contributionType}
                  onChange={(e) => setContributionType(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="improvement">Mejora de contenido existente</option>
                  <option value="addition">Agregar información faltante</option>
                  <option value="correction">Corrección de error</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tu contribución:
                </label>
                <textarea
                  value={contributionText}
                  onChange={(e) => setContributionText(e.target.value)}
                  placeholder={`Describe tu ${contributionType === 'improvement' ? 'mejora' : contributionType === 'addition' ? 'adición' : 'corrección'} para la sección "${contributionSection}"...`}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 h-32 resize-none"
                />
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
                <p className="text-yellow-800 text-sm">
                  <strong>Nota:</strong> Las contribuciones aprobadas podrán recibir recompensas en conchas de cowrie. 
                  ¡Gracias por ayudar a preservar la sabiduría de Ifá!
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleContributionSubmit}
                  disabled={!contributionText.trim()}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                    contributionText.trim()
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Enviar Contribución
                </button>
                <button
                  onClick={() => setShowContributionModal(false)}
                  className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  }

  // Default return - should not reach here
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-emerald-800 mb-4">Estado del juego no reconocido</h2>
        <button 
          onClick={() => setGameState('menu')}
          className="bg-emerald-500 text-white px-6 py-3 rounded-xl hover:bg-emerald-600 transition-colors"
        >
          Volver al Menú
        </button>
      </div>
    </div>
  );
};

export default IfaSignsGame;